import { createHash } from "node:crypto";
import { z } from "zod";
import { SCENARIO_MAP, type Scenario } from "./scenario";
import { DAY_MS } from "./window";

/**
 * W3 LESSONS (B4, B6, B16; Agent Brains 4.3 and 5.9). The lessons ledger: what
 * the system was caught doing wrong, from OBJECTIVE events only.
 *
 * Objective means a verifier block, a gate refusal, an owner rejection with a
 * reason from the closed reject list, or an owner edit that changed a figure.
 * Never a model's judgement of a thread, never an agent's own diagnosis of
 * itself. The `instruction` is a fixed template per root cause (a human-edited
 * constant below), so no model writes a word the next prompt reads.
 *
 * Precision beats recall (CLAUDE.md section 0): a source that cannot be pinned
 * on one cause is REFUSED, not guessed. Two violations on one draft, an edit
 * with no figure change (W4's stage attribution has not landed), an owner
 * clearing a draft the verifier had already killed: all refused.
 *
 * What this file does NOT do: write an artifact. `lessons` has no row in
 * `writeTiers.ts` (open question 1, T3, Kabir's), so `writeArtifactVersion`
 * refuses the kind, by design and under test. The consolidation here is pure.
 *
 * TA notes for the integrator (review 1, m7 and m8; the ledgers are not edited here):
 * m7: the B4 promote route is only a return value plus `lesson.promoted` rows; W2 must
 * consume them, and the enqueue sites and the `lessonsLoad` wiring belong to the integrator.
 * m8: the builder's nine assumptions (see W3-report.md) are not in FINDINGS.md yet; each
 * is a `## TA-NN` entry to write at integration, same format as the rest.
 */

/** Cap on lessons put in a prompt. AWAITING RATIFICATION (B6; DESIGN W3). */
export const LESSON_LOAD_CAP = 20;
/** Age at which an entry is pruned, in days. AWAITING RATIFICATION (DESIGN W3). */
export const LESSON_MAX_AGE_DAYS = 90;
/** A key seen this many times in the live window becomes a promote candidate (B4 route). AWAITING RATIFICATION (DESIGN W3; its own constant, not one of the three F-15 copies of 5). */
export const LESSON_PROMOTE_MIN_COUNT = 5;

/** The one agent whose prompt the lessons are loaded into: the reply duty. */
export const LESSON_SUBJECT_AGENT = "reply";

/** Agents whose own events are never a source: the learner does not learn from itself. */
const SELF_AGENTS: ReadonlySet<string> = new Set(["autolearn", "lessons", "ghost_labeler"]);

export const LESSON_SOURCE_KINDS = ["verifier_block", "gate_catch", "owner_reject", "owner_edit"] as const;
export type LessonSourceKind = (typeof LESSON_SOURCE_KINDS)[number];

/** Past-tense subject of each source kind, for the provenance line. */
const SOURCE_VERB: Readonly<Record<LessonSourceKind, string>> = {
  verifier_block: "verifier blocked",
  gate_catch: "gate refused",
  owner_reject: "owner rejected",
  owner_edit: "owner edited",
};

/**
 * The closed catalog. Fixed text, a human-edited constant: a model never
 * edits it. Section 0.5: nothing here mentions an offer, a deal or money off.
 */
export const LESSON_ROOT_CAUSE_TABLE = {
  verifier_empty_facts_used: {
    sourceKind: "verifier_block",
    instruction: "Answer a factual question only from a cited knowledge base fact; with no fact to cite, say you will check on it.",
  },
  verifier_unknown_fact_id: {
    sourceKind: "verifier_block",
    instruction: "Cite only knowledge base facts you were shown; never cite a fact id you were not given.",
  },
  verifier_ungrounded_number: {
    sourceKind: "verifier_block",
    instruction: "State a figure only when it matches a knowledge base fact exactly; otherwise say you will check.",
  },
  verifier_stale_price: {
    sourceKind: "verifier_block",
    instruction: "Do not quote a price from a fact marked stale; say you will check the current figure.",
  },
  verifier_memory_sourced_price: {
    sourceKind: "verifier_block",
    instruction: "A price or policy a customer mentioned earlier is not a source; use the knowledge base or say you will check.",
  },
  verifier_cumulative_concession: {
    sourceKind: "verifier_block",
    instruction: "Keep to the shop's standing terms and do not add to them as the conversation goes on.",
  },
  // TA-1378 (TA-1377 Left open 3; section 0.5). Root cause: the reply carried
  // wording about money off or something given with a purchase that no cited
  // fact states (verifier `offer_wording`). The text names no such wording, so
  // the lesson itself never puts one in front of the model.
  verifier_offer_wording: {
    sourceKind: "verifier_block",
    instruction: "Say nothing about price or what comes with a purchase beyond what a cited knowledge base fact states.",
  },
  verifier_disclosure_missing: {
    sourceKind: "verifier_block",
    instruction: "When asked whether you are a person or a bot, say plainly that you are an AI assistant for the shop.",
  },
  verifier_disclosure_unasked: {
    sourceKind: "verifier_block",
    instruction: "Do not volunteer that you are an AI unless the customer asks.",
  },
  verifier_disclosure_claims_human: {
    sourceKind: "verifier_block",
    instruction: "Never claim to be a person; if asked, say you are an AI assistant for the shop.",
  },
  verifier_card_reveal: {
    sourceKind: "verifier_block",
    instruction: "Never tell a customer what the shop has learned about them from research; speak only to what they have said.",
  },
  // Queue 2026-10-12b fix B (B3). Root cause: the model copied a placeholder from its masked input (a contact
  // pseudonym or a bracketed mask token, contactMask.ts) into the reply. The text names neither token, so the
  // lesson itself never puts one in front of the model.
  verifier_model_mask_token: {
    sourceKind: "verifier_block",
    instruction: "Address the customer without a name, and never repeat a bracketed placeholder or a customer code from the message in the reply.",
  },
  gate_ai_tell_linter: {
    sourceKind: "gate_catch",
    instruction: "Write plainly in the shop's own voice and avoid stock assistant phrasing.",
  },
  gate_verifier: {
    sourceKind: "gate_catch",
    instruction: "Cited facts can change before a reply sends; quote only what the fact says now.",
  },
  owner_reject_compliance: {
    sourceKind: "owner_reject",
    instruction: "A draft the owner rejected broke a compliance rule; when a claim or a disclosure is uncertain, ask the owner.",
  },
  owner_reject_wrong_fact: {
    sourceKind: "owner_reject",
    instruction: "The owner rejected a draft for a wrong or ungrounded fact; use only cited knowledge base facts.",
  },
  owner_reject_off_voice: {
    sourceKind: "owner_reject",
    instruction: "The owner rejected a draft for not sounding like the shop; follow the shop's voice profile closely.",
  },
  owner_reject_wrong_move: {
    sourceKind: "owner_reject",
    instruction: "The owner rejected a draft whose next step was wrong; when unsure of the next step, ask the owner.",
  },
  owner_reject_style: {
    sourceKind: "owner_reject",
    instruction: "The owner rejected a draft for its wording or length; match the shop's register and length as the owner edits it.",
  },
  owner_edit_figure: {
    sourceKind: "owner_edit",
    instruction: "The owner changed a figure before sending; state a figure only when it matches a knowledge base fact exactly.",
  },
} as const satisfies Readonly<Record<string, { sourceKind: LessonSourceKind; instruction: string }>>;

export type LessonRootCause = keyof typeof LESSON_ROOT_CAUSE_TABLE;
export const LESSON_ROOT_CAUSES = Object.keys(LESSON_ROOT_CAUSE_TABLE) as readonly LessonRootCause[];
const isRootCause = (s: string): s is LessonRootCause => Object.hasOwn(LESSON_ROOT_CAUSE_TABLE, s);

/** The reject reasons (db approvals.ts REJECT_REASONS) that say something about the draft. */
const REJECT_TO_ROOT: Readonly<Record<string, LessonRootCause>> = {
  compliance: "owner_reject_compliance",
  "wrong-fact": "owner_reject_wrong_fact",
  "off-voice": "owner_reject_off_voice",
  "wrong-move": "owner_reject_wrong_move",
  style: "owner_reject_style",
};

/**
 * The `send.withheld` (gate, reason) pairs that say something about the draft's wording.
 * Left out on purpose: verifier `verification_failed` (the same fault the draft.blocked
 * lesson already records), verifier `cited_facts_not_rechecked` (the draft is not at fault)
 * and verifier `cited_fact_changed` (W3 fix 2 B1b: the KB changed after drafting,
 * sendPath.ts:20-25, so the draft is not at fault either). `gate_verifier` therefore has no
 * producing path: TA note, it stays in the closed catalog until Kabir rules a verifier
 * reason that is the draft's own fault.
 */
const GATE_REASON_TO_ROOT: Readonly<Record<string, LessonRootCause>> = {
  "ai_tell_linter|ai_tells_present": "gate_ai_tell_linter",
};

export interface LessonEntry {
  readonly id: string;
  readonly agentId: string;
  readonly scenario: Scenario | null;
  readonly sourceKind: LessonSourceKind;
  readonly sourceEventId: string;
  readonly rootCause: LessonRootCause;
  readonly instruction: string;
  /** "verifier blocked draft d_312, verifier_ungrounded_number, 2026-08-12". */
  readonly provenance: string;
  readonly writtenAt: string;
  /** agentId|scenario|rootCause */
  readonly dedupeKey: string;
}

/** The slice of an events row a lesson reads. `draftId` is resolved by the caller (the db layer). */
export interface LessonSourceEvent {
  readonly id: string;
  readonly type: string;
  readonly agentId: string;
  readonly occurredAt: Date;
  readonly payload: unknown;
  readonly draftId: string | null;
  /**
   * G63 (MS B3: "ambiguous attribution = no lesson ... logged for human triage"): the `result` of the
   * draft's `correction.attributed` row (W4), when the caller read one. Absent or null reads as before:
   * the field only ever tightens, it never turns a refusal into a lesson.
   */
  readonly attribution?: "attributed" | "ambiguous" | null;
}

export type LessonRefusal = "not_objective" | "ambiguous_attribution" | "self_diagnosis" | "not_subject_agent";

const obj = (p: unknown): Record<string, unknown> => (typeof p === "object" && p !== null && !Array.isArray(p) ? (p as Record<string, unknown>) : {});

function isScenario(s: unknown): s is Scenario {
  return typeof s === "string" && Object.hasOwn(SCENARIO_MAP, s);
}

const sha16 = (s: string): string => createHash("sha256").update(s).digest("hex").slice(0, 16);

/** The verifier's violation codes (agents verifier.ts VerifyViolation) mapped to root causes. */
function verifierRoot(violation: unknown): LessonRootCause | null {
  if (typeof violation !== "string") return null;
  const id = `verifier_${violation}`;
  return isRootCause(id) ? id : null;
}

/** The one place an entry is assembled, so a live source and a replayed `lesson.proposed` row agree. */
function compose(i: {
  readonly sourceEventId: string;
  readonly draftId: string | null;
  readonly rootCause: LessonRootCause;
  readonly scenario: Scenario | null;
  readonly sourceAt: string;
}): LessonEntry {
  const row = LESSON_ROOT_CAUSE_TABLE[i.rootCause];
  const subject = i.draftId !== null ? `draft ${i.draftId}` : `event ${i.sourceEventId}`;
  return {
    id: `les_${sha16(i.sourceEventId)}`,
    agentId: LESSON_SUBJECT_AGENT,
    scenario: i.scenario,
    sourceKind: row.sourceKind,
    sourceEventId: i.sourceEventId,
    rootCause: i.rootCause,
    instruction: row.instruction,
    provenance: `${SOURCE_VERB[row.sourceKind]} ${subject}, ${i.rootCause}, ${i.sourceAt.slice(0, 10)}`,
    writtenAt: i.sourceAt,
    dedupeKey: `${LESSON_SUBJECT_AGENT}|${i.scenario ?? ""}|${i.rootCause}`,
  };
}

function build(e: LessonSourceEvent, rootCause: LessonRootCause, scenario: Scenario | null): LessonEntry {
  return compose({ sourceEventId: e.id, draftId: e.draftId, rootCause, scenario, sourceAt: e.occurredAt.toISOString() });
}

/** The `lesson.proposed` payload (catalog block W3 LESSONS): codes and ids; text is rebuilt on read. */
export interface LessonProposal {
  readonly lessonId: string;
  readonly agentId: string;
  readonly scenario: string | null;
  readonly sourceKind: LessonSourceKind;
  readonly sourceEventId: string;
  readonly rootCause: string;
  readonly dedupeKey: string;
  readonly sourceAt: string;
  readonly draftId: string | null;
}

export function proposalOf(e: LessonEntry, draftId: string | null): LessonProposal {
  return {
    lessonId: e.id,
    agentId: e.agentId,
    scenario: e.scenario,
    sourceKind: e.sourceKind,
    sourceEventId: e.sourceEventId,
    rootCause: e.rootCause,
    dedupeKey: e.dedupeKey,
    sourceAt: e.writtenAt,
    draftId,
  };
}

/** Rebuilds an entry from its ledger row. Throws on a root cause outside the catalog (fail loudly). */
export function entryFromProposal(p: LessonProposal): LessonEntry {
  if (!isRootCause(p.rootCause)) throw new Error(`lesson ${p.lessonId} names root cause "${p.rootCause}", which is not in the catalog`);
  return compose({
    sourceEventId: p.sourceEventId,
    draftId: p.draftId,
    rootCause: p.rootCause,
    scenario: isScenario(p.scenario) ? p.scenario : null,
    sourceAt: p.sourceAt,
  });
}

/**
 * One source event to at most one entry. Pure. A refusal names why; the caller
 * records it and writes nothing (precision beats recall).
 */
export function lessonFromEvent(e: LessonSourceEvent): LessonEntry | { readonly refused: LessonRefusal } {
  if (e.type.startsWith("lesson.") || SELF_AGENTS.has(e.agentId)) return { refused: "self_diagnosis" };
  // W3 fix 2 B1a (re-review 2): the lessons load into the reply prompt only, so a lesson comes
  // only from the reply agent's own agent-authored rows. evaluateSendPath has ten callers
  // (campaign, follow-up, reminder, escalation, review, text-back) and each writes its own
  // agent id; one operator template caught by the linter must not teach the reply agent.
  // Owner decisions (draft.approved/rejected) are attributed to "owner" by design, so they
  // are not gated on this; the draft they name is the draft the reply agent wrote.
  if ((e.type === "send.withheld" || e.type === "draft.blocked") && e.agentId !== LESSON_SUBJECT_AGENT) return { refused: "not_subject_agent" };
  const p = obj(e.payload);
  const scenario = isScenario(p["scenario"]) ? p["scenario"] : null;

  switch (e.type) {
    case "draft.blocked": {
      const v = p["violations"];
      if (!Array.isArray(v) || v.length === 0) return { refused: "not_objective" };
      const roots = v.map(verifierRoot);
      if (roots.some((r) => r === null)) return { refused: "not_objective" };
      const distinct = [...new Set(roots as LessonRootCause[])];
      if (distinct.length !== 1) return { refused: "ambiguous_attribution" };
      return build(e, distinct[0] as LessonRootCause, scenario);
    }
    case "draft.rejected": {
      if (p["verifierBlocked"] === true) return { refused: "ambiguous_attribution" };
      // G63: W4's trace rules could not pin the owner's correction on one stage, so the reject is not
      // evidence about the reply agent. No lesson; the row stays on the digest's triage line.
      if (e.attribution === "ambiguous") return { refused: "ambiguous_attribution" };
      const reason = p["reason"];
      const root = typeof reason === "string" && Object.hasOwn(REJECT_TO_ROOT, reason) ? REJECT_TO_ROOT[reason] : undefined;
      return root === undefined ? { refused: "ambiguous_attribution" } : build(e, root, scenario);
    }
    case "draft.approved": {
      if (p["editKind"] !== "substantive") return { refused: "not_objective" };
      // Released alternates are measured substantive by rule, not by an edit.
      if (p["alternate"] !== undefined) return { refused: "ambiguous_attribution" };
      // B4 (review 1): a changed figure is NOT yet attributable to the agent. The draft had
      // already passed the verifier, so its figures matched the KB; a figure the owner then
      // corrects is usually design W4 R2 (the corrected figure is not in the KB: a KB gap,
      // and the rule there is NO lesson), and only W4 R1 (the changed figure is in the active
      // KB and not in `facts_used`) pins it on the draft. Both need W4's stage attribution,
      // which has not landed. Refused for every edit until then; `owner_edit_figure` stays in
      // the closed catalog for that wiring. TA note: W4 R1/R2 stage attribution gates this
      // source; Kabir/W4 integrator to lift the refusal. Precision beats recall (section 0).
      return { refused: "ambiguous_attribution" };
    }
    // B1 (review 1): the gate catches are the `send.withheld` restraint rows that
    // sendPath.ts `withheld()` writes through `recordRestraint`, payload { gate, reason }.
    // `transmit.refused` carries a TransmitGate (window, arbiter, ...) that is never a
    // content gate, so it is deliberately not read here.
    case "send.withheld": {
      const gate = p["gate"];
      const reason = p["reason"];
      const root =
        typeof gate === "string" && typeof reason === "string" && Object.hasOwn(GATE_REASON_TO_ROOT, `${gate}|${reason}`)
          ? GATE_REASON_TO_ROOT[`${gate}|${reason}`]
          : undefined;
      return root === undefined ? { refused: "not_objective" } : build(e, root, scenario);
    }
    default:
      return { refused: "not_objective" };
  }
}

// -------------------------------------------------------------- the artifact

export const LESSONS_ARTIFACT_SCHEMA = "lessons-v1";

const SCENARIO_KEYS = Object.keys(SCENARIO_MAP) as [Scenario, ...Scenario[]];

export const lessonEntrySchema = z
  .strictObject({
    id: z.string().regex(/^les_[a-f0-9]{16}$/),
    agentId: z.string().regex(/^[a-z0-9_.:-]{1,64}$/),
    scenario: z.enum(SCENARIO_KEYS).nullable(),
    sourceKind: z.enum(LESSON_SOURCE_KINDS),
    sourceEventId: z.string().regex(/^[A-Za-z0-9_.:-]{1,128}$/),
    rootCause: z.enum(LESSON_ROOT_CAUSES as [LessonRootCause, ...LessonRootCause[]]),
    instruction: z.string().min(1),
    provenance: z.string().regex(/^\S(?:.{0,254}\S)?$/),
    writtenAt: z.iso.datetime(),
    dedupeKey: z.string().min(1).max(200),
  })
  .superRefine((e, ctx) => {
    const row = LESSON_ROOT_CAUSE_TABLE[e.rootCause];
    if (e.instruction !== row.instruction) ctx.addIssue({ code: "custom", path: ["instruction"], message: "not the root cause's fixed template" });
    if (e.sourceKind !== row.sourceKind) ctx.addIssue({ code: "custom", path: ["sourceKind"], message: "not the root cause's source kind" });
    // B3 (review 1): stored free text must never reach `charterLines`. `provenance` and
    // `dedupeKey` are byte-for-byte what compose() builds from the entry's own structured
    // fields; the one id compose() also reads, the draft id, is recovered from the line
    // under the same id grammar and then re-composed, so nothing else can ride along.
    const draft = /^[a-z ]+ draft ([A-Za-z0-9_.:-]{1,128}), /.exec(e.provenance);
    const built = compose({
      sourceEventId: e.sourceEventId,
      draftId: draft !== null ? (draft[1] as string) : null,
      rootCause: e.rootCause,
      scenario: e.scenario,
      sourceAt: e.writtenAt,
    });
    if (e.provenance !== built.provenance) ctx.addIssue({ code: "custom", path: ["provenance"], message: "not what the entry's own fields compose" });
    if (e.dedupeKey !== built.dedupeKey) ctx.addIssue({ code: "custom", path: ["dedupeKey"], message: "not what the entry's own fields compose" });
  });

export const lessonsArtifactSchema = z.strictObject({
  schema: z.literal(LESSONS_ARTIFACT_SCHEMA),
  entries: z.array(lessonEntrySchema),
});
export interface LessonsArtifact {
  readonly schema: typeof LESSONS_ARTIFACT_SCHEMA;
  readonly entries: readonly LessonEntry[];
}

export interface LessonPromotion {
  readonly dedupeKey: string;
  readonly agentId: string;
  readonly scenario: Scenario | null;
  readonly rootCause: LessonRootCause;
  readonly count: number;
  /** Newest first. */
  readonly lessonIds: readonly string[];
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
}

export type LessonPruneReason = "superseded" | "aged_out";
export interface LessonPrune {
  readonly id: string;
  readonly reason: LessonPruneReason;
  readonly successorId: string | null;
}

const newestFirst = (a: LessonEntry, b: LessonEntry): number =>
  a.writtenAt === b.writtenAt ? (a.id < b.id ? -1 : a.id > b.id ? 1 : 0) : a.writtenAt < b.writtenAt ? 1 : -1;

/**
 * The weekly consolidation. Pure and deterministic. Dedupe newest-wins per key
 * (older duplicates are superseded, naming the survivor), entries older than
 * `LESSON_MAX_AGE_DAYS` are pruned, and a key seen `LESSON_PROMOTE_MIN_COUNT`
 * or more times in the live window is a promote candidate for the learner's
 * B4 route. The cap applies at load, not here: the ledger is uncapped.
 */
export function consolidateLessons(
  open: readonly LessonEntry[],
  now: Date,
  /**
   * B2 (review 1): every `lesson.proposed` entry on record, superseded and pruned ones
   * included. The promotion count reads this, not `open`, because each weekly run
   * supersedes all but the newest of a key, so counting open entries only would let a
   * key reach 5 only if all five arrived in one week. Defaults to `open`.
   */
  history: readonly LessonEntry[] = open,
  /**
   * W3 fix 2 B2 (re-review 2): the dedupe keys that already have a `lesson.promoted` row inside
   * the 90-day window. A key promotes ONCE; later runs skip it and count it in `alreadyPromoted`.
   */
  promotedKeys: ReadonlySet<string> = new Set(),
): {
  readonly content: LessonsArtifact;
  readonly promote: readonly LessonPromotion[];
  readonly prune: readonly LessonPrune[];
  readonly alreadyPromoted: number;
} {
  let alreadyPromoted = 0;
  const cutoff = now.getTime() - LESSON_MAX_AGE_DAYS * DAY_MS;
  const seen = new Map<string, LessonEntry[]>();
  for (const e of history) {
    if (new Date(e.writtenAt).getTime() < cutoff) continue;
    const g = seen.get(e.dedupeKey);
    if (g) g.push(e);
    else seen.set(e.dedupeKey, [e]);
  }
  const prune: LessonPrune[] = [];
  const groups = new Map<string, LessonEntry[]>();
  for (const e of open) {
    if (new Date(e.writtenAt).getTime() < cutoff) {
      prune.push({ id: e.id, reason: "aged_out", successorId: null });
      continue;
    }
    const g = groups.get(e.dedupeKey);
    if (g) g.push(e);
    else groups.set(e.dedupeKey, [e]);
  }
  const survivors: LessonEntry[] = [];
  const promote: LessonPromotion[] = [];
  for (const key of [...groups.keys()].sort()) {
    const g = [...(groups.get(key) as LessonEntry[])].sort(newestFirst);
    const [head, ...rest] = g as [LessonEntry, ...LessonEntry[]];
    survivors.push(head);
    for (const r of rest) prune.push({ id: r.id, reason: "superseded", successorId: head.id });
    const all = [...(seen.get(key) ?? [])].sort(newestFirst);
    if (all.length >= LESSON_PROMOTE_MIN_COUNT && promotedKeys.has(key)) {
      alreadyPromoted += 1;
    } else if (all.length >= LESSON_PROMOTE_MIN_COUNT) {
      const first = all[0] as LessonEntry;
      const last = all[all.length - 1] as LessonEntry;
      promote.push({
        dedupeKey: key,
        agentId: first.agentId,
        scenario: first.scenario,
        rootCause: first.rootCause,
        count: all.length,
        lessonIds: all.map((x) => x.id),
        firstSeenAt: last.writtenAt,
        lastSeenAt: first.writtenAt,
      });
    }
  }
  prune.sort((a, b) => (a.reason === b.reason ? (a.id < b.id ? -1 : 1) : a.reason < b.reason ? -1 : 1));
  return { content: { schema: LESSONS_ARTIFACT_SCHEMA, entries: survivors.sort(newestFirst) }, promote, prune, alreadyPromoted };
}

// ----------------------------------------------------------------- the prompt

/** The trusted, constant header above the lesson lines. Fixed text; no customer or shop word. */
export const LESSONS_HEADER = "[LESSONS] mistakes this system was caught making; each line is a fixed rule";

/**
 * The lines the loader appends to the draft request's `charterLines`. Empty
 * when there is nothing to say, so a run with no lessons adds not even a header.
 */
export function renderLessonLines(entries: readonly LessonEntry[], overflow: number): string[] {
  if (entries.length === 0) return [];
  // W3 fix 2 B3 (section 6: stored text is data, never instructions): only the fixed template
  // of the root cause reaches the prompt. Ids and provenance stay in the artifact and the ledger
  // for explain-on-demand. The instruction is read from the table by root cause, not from the
  // entry, so even an entry that slipped past the schema cannot carry a word of its own.
  const lines = [LESSONS_HEADER, ...entries.map((e) => `- ${LESSON_ROOT_CAUSE_TABLE[e.rootCause].instruction}`)];
  if (overflow > 0) lines.push(`${overflow} more lesson${overflow === 1 ? " is" : "s are"} on record and not shown.`);
  return lines;
}
